// Utility per istanziare Choices.js con un tipo opzioni ricostruito
// Nota: non importiamo i d.ts ufficiali di Choices.
// Qui definiamo i tipi minimi realmente usati nel progetto
// e lasciamo un indice aperto per altre chiavi supportate da Choices.

// Elemento supportato da Choices
export type ChoicesElement = HTMLSelectElement | HTMLInputElement;

// Opzioni (sottoinsieme) riprese dai d.ts ufficiali e dai casi d'uso nel repo
export interface ChoicesOptions {
  // base data
  items?: (string | InputChoiceLike)[];
  choices?: InputChoiceLike[];

  // limiti e comportamento selezione
  renderChoiceLimit?: number; // -1 = nessun limite
  maxItemCount?: number; // -1 = nessun limite
  closeDropdownOnSelect?: boolean | 'auto';
  singleModeForMultiSelect?: boolean;

  // aggiunta elementi/choices
  addChoices?: boolean;
  addItems?: boolean;
  addItemFilter?: string | RegExp | ((value: string) => boolean) | null;
  addItemText?: string | ((value: string, valueRaw?: string) => string);

  // rimozione elementi
  removeItemIconText?: string | ((value: string, valueRaw?: string) => string);
  removeItemLabelText?: string | ((value: string, valueRaw?: string) => string);
  removeItems?: boolean;
  removeItemButton?: boolean;
  removeItemButtonAlignLeft?: boolean;

  // input editing e sicurezza
  editItems?: boolean;
  allowHTML?: boolean;
  allowHtmlUserInput?: boolean;
  duplicateItemsAllowed?: boolean;
  delimiter?: string;
  paste?: boolean;

  // ricerca
  searchEnabled?: boolean;
  searchChoices?: boolean;
  searchFloor?: number;
  searchResultLimit?: number;
  searchFields?: string[];

  // posizionamento e scroll
  position?: 'auto' | 'top' | 'bottom' | 'always-bottom';
  resetScrollPosition?: boolean;
  shadowRoot?: ShadowRoot | null;

  // ordinamento
  shouldSort?: boolean;
  sortChoices?: boolean;
  sortGroups?: boolean;

  // placeholder e rendering
  placeholder?: boolean;
  placeholderValue?: string | null;
  searchPlaceholderValue?: string | null;
  prependValue?: string | null;
  appendValue?: string | null;
  renderSelectedChoices?: 'auto' | 'always' | boolean;

  // testi UI
  loadingText?: string;
  noResultsText?: string | ((value: string) => string);
  noChoicesText?: string | ((value: string) => string);
  itemSelectText?: string;
  maxItemText?: string | ((maxItemCount: number) => string);
  uniqueItemText?: string | ((value: string) => string);
  customAddItemText?: string | ((value: string) => string);

  // confronto valori e classi
  valueComparer?: (choice: any, item: any) => boolean;
  classNames?: Record<string, string> | any;

  // ricerca estesa e accessibilità
  fuseOptions?: any; // IFuseOptions-like
  labelId?: string;

  // callback
  callbackOnInit?: (() => void) | null;
  callbackOnCreateTemplates?: (
    template: any,
    originalTemplates?: any,
    getClassNames?: (classNames: any) => string[]
  ) => any | null;

  // altri flag
  appendGroupInSearch?: boolean | false;

  // apertura per tutte le altre chiavi supportate da Choices
  [key: string]: any;
}

// Alias, se preferisci usare il nome singolare "Option" nella tua codebase
export type Option = ChoicesOptions;

// Tipo minimale dell'istanza, con i metodi usati nel progetto
export interface ChoicesInstance {
  setChoices: (...args: any[]) => any;
  clearChoices: (...args: any[]) => any;
  getValue: (valueOnly?: boolean) => any;
  setValue: (items: string[] | any[]) => any;
  removeActiveItemsByValue: (value: string) => any;
  destroy: () => void;
  [key: string]: any;
}

// Tipi minimi per le scelte (ripresi dal d.ts originale)
export interface InputChoiceLike {
  value: string;
  label?: string;
  selected?: boolean;
  disabled?: boolean;
  id?: number | string;
  customProperties?: any;
}
const commonOptions: Partial<ChoicesOptions> = {
  // qui vanno le stringhe tradotte in italiano
  loadingText: 'Caricamento in corso...',
  noResultsText: (value: string) => `Nessun risultato trovato per`,
  noChoicesText: 'Digita almeno un carattere per cercare',  
  itemSelectText: 'Clicca per selezionare',
  removeItemIconText: 'Rimuovi',
  removeItemLabelText: (value: string) => `Rimuovi elemento`,
  maxItemText: (maxItemCount: number) =>
    `Puoi selezionare solo ${maxItemCount} elemento${maxItemCount !== 1 ? 'i' : ''}`,
  uniqueItemText: (value: string) => `L'elemento è già stato selezionato`,
  customAddItemText: (value: string) => `Aggiungi elemento`,
  addItemText: (value: string) => `Premi Invio per aggiungere l'elemento`,
  searchPlaceholderValue: 'Cerca...',
  // altre opzioni comuni
  removeItemButton: true,
  searchEnabled: true,
   maxItemCount: 20,
}
const { Choices } = window;
/**
 * Crea una nuova istanza di Choices usando window.Choices.
 * Lancia un errore se Choices non è presente a runtime.
 */
export function createChoicesInstance(
  element: ChoicesElement,
  options: Partial<ChoicesOptions> = {}
): any {
  options = { ...commonOptions, ...options };
  return new window.Choices(element, options as any); ;
}
